import { Link } from "react-router-dom";
import { MapPin, BadgeCheck, Crown, ExternalLink } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { heroImageForCategory } from "@/lib/categoryImages";
import BusinessLogo from "@/components/BusinessLogo";
import FavoriteButton from "@/components/FavoriteButton";
import FollowButton from "@/components/FollowButton";
import ShareButton from "@/components/ShareButton";
import { trackEvent } from "@/lib/analytics";

/**
 * StorefrontHero — TBC redesign per Feb 2026 spec.
 * Cinematic on tablet/desktop (~360px), short on mobile (~200px) so commerce
 * content isn't buried below the fold. Uses business cover image or logo
 * mark on a category-tinted navy backdrop; a dark navy→transparent gradient
 * keeps the business name and action row legible over any photo.
 */
export function StorefrontHero({ business, isFavorited, onFavoriteChange }) {
  const {
    business_id: businessId, name, category, custom_category,
    is_verified, is_founding_member, founding_member_number,
    subscription_tier, is_premium,
    logo_url, image_url,
    city, state, tagline, description,
    is_black_owned = true,
    custom_cta_label, custom_cta_url,
  } = business;

  const displayCategory = category === "Other" && custom_category ? custom_category : category;
  const location = [city, state].filter(Boolean).join(", ");
  const heroBg = resolveAssetUrl(image_url) || heroImageForCategory(category);
  const teaser = tagline || (description || "").split(/[.\n]/)[0]?.slice(0, 140) || "";

  return (
    <section className="pt-4 sm:pt-6" data-testid="storefront-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-2xl overflow-hidden h-[210px] sm:h-[320px] lg:h-[380px]">
          {/* Background: cover image OR logo on category-tinted navy */}
          {logo_url ? (
            <div className="absolute inset-0 bg-tbc-navy">
              <img
                src={heroBg}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-20"
              />
            </div>
          ) : (
            <img src={heroBg} alt={name} className="absolute inset-0 w-full h-full object-cover" />
          )}

          {/* Gradient overlay — keep text readable */}
          <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy/95 via-tbc-navy/60 to-tbc-navy/10 pointer-events-none" />

          {/* Top-left badges */}
          <div className="absolute top-3 left-3 sm:top-5 sm:left-5 flex flex-wrap items-center gap-1.5">
            {displayCategory && (
              <span className="px-2.5 py-0.5 bg-tbc-gold text-tbc-navy text-[11px] sm:text-xs font-medium rounded-full">
                {displayCategory}
              </span>
            )}
            {is_founding_member && founding_member_number ? (
              <Link
                to={`/founding-100#${String(founding_member_number).padStart(3, "0")}`}
                data-testid="founding-member-badge"
                className="px-2.5 py-0.5 bg-gradient-to-r from-tbc-gold to-amber-400 text-tbc-navy text-[11px] sm:text-xs font-bold rounded-full flex items-center gap-1 shadow-sm"
              >
                <Crown className="w-3 h-3" /> Founding #{String(founding_member_number).padStart(3, "0")}
              </Link>
            ) : is_verified ? (
              <span className="px-2.5 py-0.5 bg-tbc-gold/90 text-tbc-navy text-[11px] sm:text-xs font-medium rounded-full flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> Verified
              </span>
            ) : null}
            {!is_founding_member && (is_premium || subscription_tier === "premium") && (
              <span className="px-2.5 py-0.5 bg-gradient-to-br from-gray-200 to-gray-300 text-gray-800 text-[11px] sm:text-xs font-semibold rounded-full flex items-center gap-1">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
            {is_black_owned && (
              <span
                className="px-2.5 py-0.5 bg-tbc-navy/70 border border-tbc-gold/50 text-tbc-gold text-[11px] sm:text-xs font-medium rounded-full backdrop-blur-sm"
                data-testid="black-owned-badge"
              >
                Black Owned
              </span>
            )}
          </div>

          {/* Logo mark on mobile/tablet — centered above the name */}
          {logo_url && (
            <div className="absolute inset-x-0 top-3 sm:top-8 flex items-center justify-center pointer-events-none">
              <div className="scale-75 sm:scale-100">
                <BusinessLogo
                  name={name}
                  logoUrl={logo_url}
                  size="lg"
                  shape="rounded"
                />
              </div>
            </div>
          )}

          {/* Bottom: name, tagline, actions */}
          <div className="absolute inset-x-0 bottom-0 p-3 sm:p-5 lg:p-7">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <h1
                  className="font-heading text-white leading-[1.05] drop-shadow-sm"
                  style={{ fontSize: "clamp(1.35rem, 4.5vw, 2.75rem)" }}
                  data-testid="business-name"
                >
                  {name}
                </h1>
                {teaser && (
                  <p className="mt-1 text-white/80 font-body text-xs sm:text-sm max-w-2xl line-clamp-1 sm:line-clamp-2">
                    {teaser}
                  </p>
                )}
                {location && (
                  <p className="mt-1 text-tbc-gold text-[11px] sm:text-xs font-body inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {location}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                <div className="hidden sm:block">
                  <FollowButton targetType="business" targetId={businessId} variant="compact" />
                </div>
                <FavoriteButton
                  businessId={businessId}
                  initial={isFavorited}
                  size="sm"
                  onChange={onFavoriteChange}
                />
                {custom_cta_label && custom_cta_url && (
                  <a
                    href={custom_cta_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent(businessId, "cta_click", { metadata: { label: custom_cta_label } })}
                    data-testid="custom-cta-button"
                    className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-colors"
                  >
                    {custom_cta_label}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <ShareButton
                  title={name}
                  text={`Check out ${name} on The Blk Community`}
                  variant="icon"
                  testId="share-business-button"
                  onShare={() => trackEvent(businessId, "share_click")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default StorefrontHero;
